import { useForm } from "@tanstack/react-form";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/Button";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { Input } from "../../components/Input";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { FormActions } from "../../components/FormActions";
import { SelectInput } from "../../components/SelectInput";
import { Country } from "@KassaroAB/shared/types/enums/Country";
import { Language } from "@KassaroAB/shared/types/enums/Language";
import { Form } from "../../components/Form";
import { Toggle } from "../../components/Toggle";
import { InfoBox } from "../../components/InfoBox";
import type { RestStore } from "@KassaroAB/shared/types/store";
import type { Currency } from "@KassaroAB/shared/types/enums/currency";
import { CountryCurrency } from "@KassaroAB/shared/utils/money";

type StoreFormValues = {
  name: string;
  displayName: string;
  orgNumber: string;
  street: string;
  postalcode: string;
  city: string;
  contactPerson: string;
  contactPhone: string;
  contactEmail: string;
  country: Country;
  currency: Currency;
  language: Language;
  timeZone: string;
  active: boolean;
};

const ErrorMessage = styled.div`
  color: ${colors.danger};
  font-size: ${fontSizes.small}px;
  margin-top: ${spacing.x05}px;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.x2}px;
`;

const HintText = styled.p`
  margin: ${spacing.x05}px 0 0;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const OrgReadOnlyHint = styled(HintText)`
  margin: 0 0 ${spacing.x2}px;
`;

type StoreFormProps = {
  store?: RestStore;
  onSubmit: (values: StoreFormValues) => void;
  onCancel?: () => void;
  isLoading?: boolean;
  submitLabel: string;
  submitLoadingLabel: string;
  readOnlyOrgNumber?: string;
  formKey?: string;
  errorMessage?: string;
};

export const StoreForm = ({
  store,
  onSubmit,
  onCancel,
  isLoading,
  submitLabel,
  submitLoadingLabel,
  readOnlyOrgNumber,
  formKey,
  errorMessage,
}: StoreFormProps) => {
  const { t } = useTranslation();
  const showLocaleFields = !store;

  const form = useForm({
    defaultValues: {
      name: store?.name ?? "",
      displayName: store?.displayName ?? "",
      orgNumber: store?.orgNumber ?? "",
      street: store?.street ?? "",
      postalcode: store?.postalcode ?? "",
      city: store?.city ?? "",
      contactPerson: store?.contactPerson ?? "",
      contactPhone: store?.contactPhone ?? "",
      contactEmail: store?.contactEmail ?? "",
      country: store?.country ?? Country.SWEDEN,
      language: store?.language ?? Language.SV,
      timeZone: store?.timeZone ?? "Europe/Stockholm",
      active: store?.active ?? true,
    },
    onSubmit: ({ value }) => {
      onSubmit({
        ...value,
        currency: CountryCurrency[value.country],
      });
    },
  });

  return (
    <Form
      key={formKey}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      {errorMessage ? <InfoBox variant="error">{errorMessage}</InfoBox> : null}

      <form.Field
        name="name"
        validators={{
          onChange: ({ value }) =>
            !value?.trim() ? t("storeForm.nameRequired") : undefined,
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("storeForm.storeName")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("storeForm.storeNamePlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      {readOnlyOrgNumber !== undefined ? (
        <>
          <FormGroup>
            <FormLabel>{t("storeForm.organizationNumber")}</FormLabel>
            <Input type="text" value={readOnlyOrgNumber} readOnly disabled />
          </FormGroup>
          <OrgReadOnlyHint>{t("overview.orgNumberReadOnlyHint")}</OrgReadOnlyHint>
        </>
      ) : (
        <form.Field
          name="orgNumber"
          validators={{
            onChange: ({ value }) =>
              !value?.trim() ? t("storeForm.organizationNumberRequired") : undefined,
          }}
        >
          {(field) => (
            <FormGroup>
              <FormLabel>{t("storeForm.organizationNumber")}</FormLabel>
              <Input
                type="text"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                required
                placeholder={t("storeForm.organizationNumberPlaceholder")}
              />
              {field.state.meta.errors.length > 0 && (
                <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
              )}
            </FormGroup>
          )}
        </form.Field>
      )}

      <form.Field
        name="displayName"
        validators={{
          onChange: ({ value }) =>
            !value?.trim() ? t("storeForm.displayNameRequired") : undefined,
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("storeForm.displayName")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("storeForm.displayNamePlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="street"
        validators={{
          onChange: ({ value }) =>
            !value?.trim() ? t("storeForm.streetRequired") : undefined,
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("storeForm.street")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("storeForm.streetPlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="postalcode"
        validators={{
          onChange: ({ value }) =>
            !value?.trim() ? t("storeForm.postalCodeRequired") : undefined,
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("storeForm.postalCode")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("storeForm.postalCodePlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="city"
        validators={{
          onChange: ({ value }) =>
            !value?.trim() ? t("storeForm.cityRequired") : undefined,
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("storeForm.city")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("storeForm.cityPlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="contactPerson"
        validators={{
          onChange: ({ value }) =>
            !value?.trim() ? t("storeForm.contactPersonRequired") : undefined,
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("storeForm.contactPerson")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("storeForm.contactPersonPlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="contactEmail"
        validators={{
          onChange: ({ value }) => {
            if (!value?.trim()) return t("storeForm.contactEmailRequired");
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value.trim())) return t("storeForm.emailInvalid");
            return undefined;
          },
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("storeForm.contactEmail")}</FormLabel>
            <Input
              type="email"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("storeForm.contactEmailPlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="contactPhone"
        validators={{
          onChange: ({ value }) =>
            !value?.trim() ? t("storeForm.contactPhoneRequired") : undefined,
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("storeForm.contactPhone")}</FormLabel>
            <Input
              type="tel"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("storeForm.contactPhonePlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      {showLocaleFields ? (
        <>
          <form.Field name="country">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("storeForm.country")}</FormLabel>
                <SelectInput
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value as Country)}
                  onBlur={field.handleBlur}
                  required
                >
                  {Object.values(Country).map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </SelectInput>
              </FormGroup>
            )}
          </form.Field>

          <form.Field name="language">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("storeForm.language")}</FormLabel>
                <SelectInput
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value as Language)}
                  onBlur={field.handleBlur}
                  required
                >
                  {Object.values(Language).map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </SelectInput>
              </FormGroup>
            )}
          </form.Field>
        </>
      ) : null}

      <form.Field name="active">
        {(field) => (
          <FormGroup>
            <ToggleRow>
              <FormLabel>{t("storeForm.storeActive")}</FormLabel>
              <Toggle
                checked={field.state.value}
                onChange={(v) => field.handleChange(v)}
              />
            </ToggleRow>
            <HintText>{t("storeForm.storeActiveHint")}</HintText>
          </FormGroup>
        )}
      </form.Field>

      <FormActions>
        {onCancel && (
          <Button type="button" $color="secondary" onClick={onCancel} disabled={isLoading}>
            {t("common.cancel")}
          </Button>
        )}
        <Button type="submit" disabled={isLoading}>
          {isLoading ? submitLoadingLabel : submitLabel}
        </Button>
      </FormActions>
    </Form>
  );
};
